<template>
  <div>
    <exo-table :root="_self" :formWidth="1300">
      <exo-form :root="_self" :clear="false">
        <v-row>
          <v-col cols="6">
            <exo-input-name :root="_self" gt_PerformanceAnalysis-v-step="5" />
            <exo-input-ref-multiple
              :root="_self"
              name="homogeneousConstellationGeometries"
              :isParametric="false"
              type="homogeneousConstellationGeometries"
              componentRoute="/analyticalconstellationhomogeneousgeometry"
              gt_PerformanceAnalysis-v-step="6"
            >
              <exo-help-dialog title="Constellation geometries">
                <tooltipConstellationGeometry />
              </exo-help-dialog>
            </exo-input-ref-multiple>
            <div gt_PerformanceAnalysis-v-step="25" />
            <div gt_PerformanceAnalysis-v-step="26" />
            <div gt_PerformanceAnalysis-v-step="37" />
            <exo-geometries-display-table
              :items="inputs.homogeneousConstellationGeometries"
            ></exo-geometries-display-table>
          </v-col>
          <v-col cols="6" sm6>
            <exo-card-form class="ml-2" label="Ground traces"> </exo-card-form>
            <v-row v-if="this.tracesResults">
              <ConstellationHeterogeneousCesiumViewer
                :traces="this.tracesResults"
                :loading="this.loading"
              ></ConstellationHeterogeneousCesiumViewer>
            </v-row>
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
    <v-tour
      name="performanceAnalysisTourConstellationHeterogeneous"
      :steps="steps_constellation"
      :callbacks="customCallbacks"
      :options="noSkipStepOption"
    ></v-tour>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import { mapGetters, mapActions } from 'vuex'
import dataTools from '../../tools/data'
import fieldsDescription from '../../tools/fieldsDescription'
import tooltipConstellationGeometry from '../../components/Tooltips/ConstellationModule/tooltipConstellationGeometry'
import ConstellationHeterogeneousCesiumViewer from './ConstellationHeterogeneousCesiumViewer.vue'
import guidedTutorialPerformanceAnalysis, {
  steps_constellation
} from '../../components/GuidedTutorials/guidedTutorialPerformanceAnalysis.js'
import { noSkipStepOption } from '../../components/GuidedTutorials/guidedTutorialConstraints.js'

constellationComponentsLoader()

export default {
  name: 'AnalyticalConstellationHeterogeneous',

  mixins: [formMixin, formAnalyticalMixin],

  components: {
    tooltipConstellationGeometry,
    ConstellationHeterogeneousCesiumViewer
  },

  mounted: function () {
    this.refreshReferences()
    this.computeEphemerisTraces()
    if (this.gt_PerformanceAnalysis.isActive) {
      guidedTutorialPerformanceAnalysis.beginConstellationTour(this)
    }
  },

  beforeDestroy() {
    try {
      Plotly.purge(this.drawingDomName)
    } catch (_) {}
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        { value: 'numberOfPlanes', text: 'Number of planes', sortable: true },
        {
          value: 'numberOfSpacecrafts',
          text: 'Number of spacecrafts',
          sortable: true
        },
        {
          value: 'altitudeRange',
          text: 'Altitude range' + this.getDefaultUnitLabel('altitudeRange'),
          sortable: true
        },
        {
          value: 'inclinationRange',
          text:
            'Inclination range' + this.getDefaultUnitLabel('inclinationRange'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: [] },
      showModal: false,

      tracesResults: [],

      homogeneousConstellationGeometriessNotParametrics: [],

      errorTrace: '',

      loading: false,

      steps_constellation: steps_constellation,
      noSkipStepOption: noSkipStepOption,
      customCallbacks: {
        onNextStep: (currentStep) => {
          guidedTutorialPerformanceAnalysis.onNextStepConstellation(
            currentStep,
            this
          )
        },
        onSkip: () => {
          guidedTutorialPerformanceAnalysis.stopAllTours(this)
        },
        onFinish: () => {
          guidedTutorialPerformanceAnalysis.onFinishSubmit(this)
        }
      },

      inputs: {
        name: this.getDefaultValue('name'),
        visualizationDate: this.getDefaultValue('visualizationDate'),
        homogeneousConstellationGeometries: [],
        displayForm: false
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    refreshReferences() {
      this.refreshGeometries()
    },

    refreshGeometries() {
      let self = this
      this.$http
        .get(this.getAPI('subconstellationdefinitions'))
        .then((response) => {
          self.homogeneousConstellationGeometriessNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of homogeneous constellation geometries. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    async computeEphemerisTraces() {
      this.tracesResults = []
      this.loading = true
      if (this.inputs?.homogeneousConstellationGeometries) {
        let constellationGeometries =
          this.inputs.homogeneousConstellationGeometries
        let traces = []
        let payloads = []
        for (let geometry of constellationGeometries) {
          if (!geometry.visualizationDate) {
            if (!this.inputs.visualizationDate) {
              geometry.visualizationDate = this.inputs.visualizationDate
            } else {
              geometry.visualizationDate = '2022-01-01T12:00:00.000Z'
            }
          }

          let data = await this.$http
            .post('/physics/constellationorbittraces', geometry)
            .then(function (response) {
              return response.data
            })
            .catch((e) => {
              this.errorTrace = this.getErrorMessage(e)
              return null
            })
          if (data == null) {
            traces = null
          } else {
            this.errorTrace = ''
            traces = traces.concat([data.ephemeridesTraces])
            payloads = payloads.concat(data?.payload?.fieldOfView)
          }
        }
        if (traces == null) {
          this.tracesResults = null
        } else {
          let result = {
            traces: traces,
            payloads: payloads
          }
          this.tracesResults = result.traces
        }
      }
      this.loading = false
    },

    getRequiredPropertiesList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      return requiredList
    },

    getOptionalPropertiesList() {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getCurrentFormTitle() {
      return 'Constellation'
    },

    getCurrentFormType() {
      return 'ConstellationDefinition'
    },

    getRefList(param) {
      if (param === 'homogeneousConstellationGeometries') {
        return this.homogeneousConstellationGeometriessNotParametrics
      }
    },

    getCustomFields() {
      return [
        'numberOfPlanes',
        'numberOfSpacecrafts',
        'altitudeRange',
        'inclinationRange'
      ]
    },

    getConstellationInfos(item) {
      const numberOfGeometries = item.homogeneousConstellationGeometries.length
      let infos = `${numberOfGeometries} ${
        numberOfGeometries === 1 ? ' geometry' : ' geometries'
      } selected \n`
      for (let i = 0; i < numberOfGeometries; i++) {
        const currentItem = item.homogeneousConstellationGeometries[i]
        if (currentItem) {
          infos +=
            (numberOfGeometries === 1
              ? ''
              : this.getRightNumberToDisplay(i + 1)) +
            'Geometry : ' +
            currentItem.displayedName +
            '\n'
          infos +=
            dataTools.getInfos(currentItem) +
            (i < numberOfGeometries - 1 ? '\n' : '')
        }
      }
      return infos
    },

    getCustomInfos(item) {
      return this.getConstellationInfos(item)
    },

    getRightNumberToDisplay(number) {
      switch (number) {
        case 0:
          return 'No '
        case 1:
          return '1st '
        case 2:
          return '2nd '
        case 3:
          return '3rd '
        default:
          return `${number}th `
      }
    },

    displayCustomValue(data, field) {
      switch (field) {
        case 'numberOfPlanes':
          return this.computeNumberOfPlanes(data)
        case 'numberOfSpacecrafts':
          return this.computeNumberOfSpacecrafts(data)
        case 'altitudeRange':
          return this.computeAltitudeRange(data)
        case 'inclinationRange':
          return this.computeInclinationRange(data)
      }
      return ''
    },

    computeNumberOfPlanes(data) {
      let geometries = data.homogeneousConstellationGeometries.filter(
        (geometry) => geometry != null
      )
      let numberOfPlanes = 0
      for (let geometry of geometries) {
        numberOfPlanes = numberOfPlanes + geometry.numberOfPlanes
      }
      return numberOfPlanes
    },

    computeNumberOfSpacecrafts(data) {
      let geometries = data.homogeneousConstellationGeometries.filter(
        (geometry) => geometry != null
      )
      let numberOfSpacecrafts = 0
      for (let geometry of geometries) {
        numberOfSpacecrafts =
          numberOfSpacecrafts +
          geometry.numberOfPlanes * geometry.numberOfSpacecraftsPerPlane
      }
      return numberOfSpacecrafts
    },

    computeAltitudeRange(data) {
      let geometries = data.homogeneousConstellationGeometries.filter(
        (geometry) => geometry != null
      )
      let minAltitude = 10000000000
      let maxAltitude = 0
      for (let geometry of geometries) {
        let currentAltitude = Math.round(geometry.altitude * 0.001 * 1e3) / 1e3
        if (currentAltitude < minAltitude) {
          minAltitude = currentAltitude
        }
        if (currentAltitude > maxAltitude) {
          maxAltitude = currentAltitude
        }
      }
      if (minAltitude === maxAltitude) {
        return minAltitude
      }
      if (minAltitude === 10000000000 && maxAltitude === 0) {
        return 'undefined'
      } else {
        return [minAltitude, maxAltitude]
      }
    },

    computeInclinationRange(data) {
      let geometries = data.homogeneousConstellationGeometries.filter(
        (geometry) => geometry != null
      )
      let minInclination = 181
      let maxInclination = 0
      for (let geometry of geometries) {
        let currentInclination = dataTools.rad2deg(geometry.inclination)
        if (currentInclination < minInclination) {
          minInclination = Math.round(currentInclination * 1e2) / 1e2
        }
        if (currentInclination > maxInclination) {
          maxInclination = Math.round(currentInclination * 1e2) / 1e2
        }
      }
      if (minInclination === maxInclination) {
        return minInclination
      }
      if (minInclination === 181 && maxInclination === 0) {
        return 'undefined'
      } else {
        return [minInclination, maxInclination]
      }
    },
    isExoInputMultiple(item) {
      return item === 'homogeneousConstellationGeometries'
    }
  },
  computed: {
    ...mapGetters(['currentMission', 'gt_PerformanceAnalysis'])
  },
  watch: {
    'inputs.homogeneousConstellationGeometries': function () {
      setTimeout(() => {
        this.computeEphemerisTraces()
      }, 200)
    },

    'inputs.displayForm': {
      handler(show) {
        if (!show) this.tracesResults = []
      }
    },
    deep: true
  }
}
</script>
